import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Smile, Leaf, Frown, CloudRain, Flame, Moon, Volume2, ArrowRight, Sparkles, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { speak } from "@/lib/speak";
import { useAutoplay } from "@/lib/useAutoplay";
import { ZOOD_MASCOT } from "@/lib/brand";

const MOOD_ICON = { happy: Smile, calm: Leaf, sad: Frown, worried: CloudRain, angry: Flame, tired: Moon };
const QUESTION = "How do you feel today?";

export default function FeelingsCheckIn({ onPoints }) {
  const navigate = useNavigate();
  const [autoplay] = useAutoplay();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");
  const [justEarned, setJustEarned] = useState(0);

  useEffect(() => {
    api.checkinToday().then((d) => {
      setState(d);
      if (!d.checked_in && autoplay) speak(QUESTION);
    }).catch(() => setState(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state) return null;

  const pick = async (m) => {
    setBusy(m.id);
    speak(m.say);
    try {
      const r = await api.submitCheckin(m.id);
      setState((s) => ({ ...s, checked_in: true, mood: r.mood, tip: r.tip, suggestion: r.suggestion }));
      setJustEarned(r.points_earned);
      if (r.points_earned && onPoints) onPoints(r.points_earned);
      setTimeout(() => speak(r.tip), 900);
    } catch (_) {} finally { setBusy(""); }
  };

  const mood = state.moods.find((x) => x.id === state.mood);
  const MoodIcon = mood ? MOOD_ICON[mood.id] : Heart;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="mt-6 overflow-hidden rounded-3xl border-2 border-[#E11D48]/20 bg-white" data-testid="feelings-checkin">
      <div className="flex items-center gap-4 bg-[#FFE4E6]/60 px-5 py-4">
        <img src={ZOOD_MASCOT} alt="" className="h-14 w-14 shrink-0 object-contain drop-shadow" />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#E11D48]">Daily check-in</div>
          <div className="font-heading text-xl font-extrabold text-[#1F2937] sm:text-2xl" data-testid="checkin-question">
            {state.checked_in && mood ? `Today you feel ${mood.label.toLowerCase()}` : QUESTION}
          </div>
        </div>
        <button onClick={() => speak(state.checked_in && state.tip ? state.tip : QUESTION)} data-testid="checkin-speak"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-[#E11D48] shadow-sm hover:bg-[#FFE4E6]" aria-label="Read aloud">
          <Volume2 size={20} />
        </button>
      </div>

      <AnimatePresence mode="wait">
        {!state.checked_in ? (
          <motion.div key="pick" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid grid-cols-3 gap-3 p-5 sm:grid-cols-6">
            {state.moods.map((m) => {
              const Icon = MOOD_ICON[m.id] || Heart;
              return (
                <motion.button key={m.id} whileHover={{ y: -3 }} whileTap={{ scale: 0.95 }} onClick={() => pick(m)} disabled={!!busy}
                  data-testid={`mood-${m.id}`}
                  className="flex flex-col items-center gap-2 rounded-2xl border-2 bg-white p-3 transition-shadow hover:shadow-md disabled:opacity-60"
                  style={{ borderColor: `${m.color}55` }}>
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: m.color }}><Icon size={28} strokeWidth={2.2} /></span>
                  <span className="font-heading text-sm font-extrabold text-[#1F2937]">{m.label}</span>
                </motion.button>
              );
            })}
          </motion.div>
        ) : (
          <motion.div key="done" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-center gap-4 p-5" data-testid="checkin-done">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: mood?.color || "#E11D48" }}><MoodIcon size={28} strokeWidth={2.2} /></span>
            <div className="min-w-0 flex-1">
              <p className="font-body text-base text-[#1F2937]" data-testid="checkin-tip">{state.tip}</p>
              {justEarned > 0 && <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#F59E0B]/15 px-2.5 py-0.5 text-xs font-bold text-[#B45309]" data-testid="checkin-points"><Sparkles size={12} /> +{justEarned} points for checking in</div>}
            </div>
            {state.suggestion && (
              <button onClick={() => navigate(`/learn/${state.suggestion.lesson_id}`)} data-testid="checkin-suggestion"
                className="group inline-flex items-center gap-3 rounded-2xl border-2 bg-white px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                style={{ borderColor: state.suggestion.subject_color }}>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: state.suggestion.subject_color }}>Zoodie suggests</div>
                  <div className="font-heading text-sm font-extrabold text-[#1F2937]">{state.suggestion.title}</div>
                </div>
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" style={{ color: state.suggestion.subject_color }} />
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
